// Program monitor: the preview to the right of the media bin. Owns a canvas that
// the VideoCompositor draws the composited frame into, letterboxed to the
// project's sequence aspect ratio. Fills the top row; height set by the layout.
import { Store } from "../state/store";
import { VideoCompositor } from "../video/compositor";
import { el, fmtTime, fmtTimecode } from "../util";

export class Monitor {
  el = el("div", { class: "monitor" });
  private stage = el("div", { class: "monitor-stage" });
  private canvas = document.createElement("canvas");
  private tc = el("span", { class: "monitor-tc", text: "00:00:00:00" });
  private resLabel = el("span", { class: "hint" });
  comp: VideoCompositor;
  private dpr = window.devicePixelRatio || 1;
  /** Transport row under the picture (filled by main), NLE-style. */
  controls = el("div", { class: "monitor-controls" });

  constructor(private store: Store) {
    this.stage.append(this.canvas);
    const bar = el("div", { class: "monitor-bar" }, [
      el("b", { text: "Program" }),
      el("div", { class: "spacer" }),
      this.tc,
      this.resLabel,
    ]);
    // Height is governed by the top-row layout; the monitor just fills it.
    this.el.append(bar, this.stage, this.controls);

    this.comp = new VideoCompositor(store, this.canvas);
    new ResizeObserver(() => this.layout()).observe(this.stage);
    store.on("change", ({ scope }) => { if (scope === "project") { this.layout(); this.updateLabel(); } });
    requestAnimationFrame(() => { this.layout(); this.updateLabel(); });
  }

  private updateLabel() {
    const p = this.store.project;
    this.resLabel.textContent = `${p.width}×${p.height} · ${p.fps}fps`;
  }

  private lastCss = "";
  /** Size the canvas backing store to the largest frame-aspect rect fitting the stage.
   *  Only writes when the target size changes — writing canvas styles inside a
   *  ResizeObserver can otherwise feed back and keep layout perpetually dirty. */
  private layout() {
    const sw = this.stage.clientWidth, sh = this.stage.clientHeight;
    if (sw <= 0 || sh <= 0) return;
    const p = this.store.project;
    const ar = p.width / p.height;
    let w = sw, h = sw / ar;
    if (h > sh) { h = sh; w = sh * ar; }
    w = Math.floor(w); h = Math.floor(h);
    const sig = `${w}x${h}`;
    if (sig === this.lastCss) return;
    this.lastCss = sig;
    this.canvas.style.width = w + "px";
    this.canvas.style.height = h + "px";
    this.canvas.width = Math.max(1, Math.round(w * this.dpr));
    this.canvas.height = Math.max(1, Math.round(h * this.dpr));
    this.comp.invalidate(); // resizing clears the canvas
  }

  /** Called from the main rAF loop. */
  private lastTc = "";
  render(pos: number, playing: boolean) {
    this.comp.render(pos, playing);
    const tc = fmtTimecode(pos, this.store.project.fps);
    if (tc !== this.lastTc) { this.lastTc = tc; this.tc.textContent = tc; this.tc.title = fmtTime(pos); }
  }
}
